/* eslint-disable i18next/no-literal-string -- . */
import {
  GetNextPayoutsDocument,
  type PayoutDtoFragment,
  PayoutStatus,
  useApprovePayoutMutation,
} from '@/affiliate-dashboard/graphql';
import { Button } from '@/components/base/button';
import { Label } from '@/components/base/label';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import { type IntlShape } from 'react-intl';

const columnHelper = createColumnHelper<PayoutDtoFragment | null>();

export const defaultColumns = (
  intl: IntlShape,
  { t }: { t: (key: string) => string },
) => [
  columnHelper.accessor('createdAt', {
    id: 'createdAt	',
    header: () => t('table.Date'),
    cell: (info) => (
      <div className="flex flex-col">
        <span>{format(info.getValue() ?? '', 'dd MMM yyyy HH:mm')} </span>
        <span>{info.row.original?.id} </span>
      </div>
    ),
  }),
  columnHelper.accessor('amount', {
    id: 'amount	',
    header: () => t('table.Amount'),
    cell: (props) => {
      return (
        <div className="flex flex-col">
          <span>
            {intl.formatNumber(props.getValue() ?? 0, {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
      );
    },
  }),

  columnHelper.accessor('email', {
    id: 'email	',
    header: () => t('table.User'),
    cell: (info) => {
      return (
        <Link
          className="group flex w-full items-center justify-start gap-3"
          to={`/dashboard/admin/promoter-detail/${info.row.original?.email}`}
        >
          <span className="max-w-44 truncate group-hover:underline">
            {info.getValue()}
          </span>
          <FontAwesomeIcon
            className="transition-all duration-200 group-hover:translate-x-2"
            icon={faArrowRight}
          />
        </Link>
      );
    },
  }),

  columnHelper.accessor('status', {
    id: 'status	',
    header: () => t('table.Status'),
    cell: (info) => {
      const labelVariant: {
        [key in PayoutStatus | '']: 'green' | 'gray' | 'default';
      } = {
        [PayoutStatus.Paid]: 'green',
        [PayoutStatus.Declined]: 'gray',
        [PayoutStatus.Requested]: 'default',
        '': 'default',
      };

      return (
        <div className="flex w-full items-center gap-3">
          <Label
            className="px-2 py-1"
            variant={labelVariant[info.getValue() ?? '']}
          >
            {info.getValue()}
          </Label>
        </div>
      );
    },
  }),
  columnHelper.accessor('id', {
    id: 'id	',
    header: () => '',
    cell: (info) => (
      <div className="flex w-full items-center justify-end gap-3">
        <ApprovePayoutButton payoutId={info.getValue() ?? ''} />
      </div>
    ),
    enableColumnFilter: false,
    enableSorting: false,
  }),
];

function ApprovePayoutButton({ payoutId }: { payoutId: string }): JSX.Element {
  const [approvePayout, { loading: approvePayoutLoading }] =
    useApprovePayoutMutation();

  async function onApprovePayout(): Promise<void> {
    try {
      const { data: payoutData } = await approvePayout({
        variables: {
          payoutId,
        },
        refetchQueries: [{ query: GetNextPayoutsDocument }],
      });
      enqueueSnackbar({
        message: `Payout ${payoutData?.approvePayout?.id} of ${payoutData?.approvePayout?.amount} has been approved`,
        variant: 'success',
      });
    } catch (e) {
      if (e instanceof Error) {
        enqueueSnackbar({
          message: e.message,
          variant: 'error',
        });
      }
    }
  }

  return (
    <Button
      variant="outlined"
      size="xsmall"
      onClick={(event) => {
        event.stopPropagation();
        onApprovePayout();
      }}
      loading={approvePayoutLoading}
      type="button"
    >
      Approve
    </Button>
  );
}
